import type {
  CheckPasswordStrengthRequest,
  CheckPasswordStrengthResponse,
  ForgotPasswordRequest,
  LoginRequest,
  RegisterUserRequest,
  TokenPair,
  UpdatePasswordRequest,
} from '@compass/client/types/auth';
import type { User } from '@compass/client/types/user';
import { authService } from '@services/api';
import { useStore } from '@store/index';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { sanitizeRedirectUrl } from '@utils/urlValidation';
import { saveCurrentUser } from '@utils/user';
import { normalizeEmail } from '@utils/validation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { setSentryUser } from '@/config/sentry';
import { logout } from '@/services/auth.session';

interface MfaState {
  required: boolean;
  challengeToken: string;
  methods: string[];
}

/**
 * Saves auth credentials to cookie and sets up Sentry user context.
 * Used by login, registration, and SSO flows.
 */
export const saveAuthCredentials = (user: User, tokenPair: TokenPair | undefined) => {
  const accessToken = tokenPair?.access_token;
  if (!accessToken || !user) return false;

  // Normalize email to lowercase for consistency
  const normalizedEmail = normalizeEmail(user.email || '');

  // Save access_token and user info to cookie for API requests
  const userData = {
    user: {
      id: user.id,
      name: user.name,
      email: normalizedEmail,
    },
    access_token: accessToken,
  };
  saveCurrentUser(userData, 7); // Save for 7 days

  // Set Sentry user context for error tracking
  if (user.id) {
    setSentryUser({
      id: user.id,
      email: normalizedEmail,
      username: user.name,
    });
  }

  return true;
};

export const useLogin = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, setIsAuthenticated } = useStore();

  const [mfaState, setMfaState] = useState<MfaState | null>(null);

  const completeLogin = useCallback(
    (user: User, tokenPair: TokenPair) => {
      if (user && saveAuthCredentials(user, tokenPair)) {
        setUser(user);
        setIsAuthenticated(true);
      }

      // Invalidate all queries to refetch with new auth
      queryClient.invalidateQueries();

      toast.success('Login successful');

      // Check for redirect in query parameters first, then state
      const params = new URLSearchParams(location.search);
      const redirectParam = params.get('redirect');
      const stateRedirect = location.state?.from?.pathname;

      // Validate and sanitize the redirect URL to prevent open redirect attacks
      const safeRedirectUrl = sanitizeRedirectUrl(redirectParam || stateRedirect);

      // Navigate to the safe redirect URL or default to root
      navigate(safeRedirectUrl, { replace: true });
    },
    [queryClient, navigate, location, setUser, setIsAuthenticated],
  );

  const clearMfaState = useCallback(() => setMfaState(null), []);

  const loginMutation = useMutation({
    mutationFn: (credentials: LoginRequest) => authService.login(credentials),
    onSuccess: data => {
      if (data.mfa_required && data.mfa_challenge_token) {
        // MFA required — don't save credentials, show MFA form
        setMfaState({
          required: true,
          challengeToken: data.mfa_challenge_token,
          methods: data.mfa_methods || [],
        });
        return;
      }

      // Normal login (no MFA)
      if (data.user && data.token_pair) {
        completeLogin(data.user, data.token_pair);
      }
    },
    onError: (error: any) => {
      // Check if the error response indicates password is required
      if (error?.response?.data?.error === 'password_required') {
        // Navigate to settings page where user can update password
        // toast.info('Please set your password in Settings');
        // navigate('/settings', { replace: true });
      }
    },
  });

  return {
    ...loginMutation,
    mfaState,
    clearMfaState,
    completeLogin,
  };
};

export const useUpdatePassword = () => {
  return useMutation({
    mutationFn: (data: UpdatePasswordRequest) => authService.updatePassword(data),
    onError: (error: any) => {
      // Error handling is done in the component
      console.error('Password update failed:', error);
    },
  });
};

export const useForgotPassword = () => {
  return useMutation({
    mutationFn: (data: ForgotPasswordRequest) => authService.forgotPassword(data),
    onError: (error: any) => {
      // Error handling is done in the component
      console.error('Forgot password request failed:', error);
    },
  });
};

export const useRegister = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setUser, setIsAuthenticated } = useStore();

  return useMutation({
    mutationFn: (data: RegisterUserRequest) => authService.registerUser(data),
    onSuccess: ({ user, token_pair }) => {
      if (user && saveAuthCredentials(user, token_pair)) {
        setUser(user);
        setIsAuthenticated(true);
      }

      // Invalidate all queries to refetch with new auth
      queryClient.invalidateQueries();

      // Navigate to complete-signup page to poll for org/program assignments
      navigate('/complete-signup', { replace: true });
    },
    onError: (error: any) => {
      // Error handling is done in the component
      console.error('Registration failed:', error);
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setUser, setIsAuthenticated, reset } = useStore();

  return async () => {
    // Clear auth from service first (removes cookie)
    await logout();

    // Reset Zustand store
    setUser(null);
    setIsAuthenticated(false);
    reset();

    // Clear all queries
    queryClient.clear();

    // Navigate to login page with replace to prevent back navigation
    navigate('/login', { replace: true });
  };
};

/**
 * Hook for checking password strength using the backend API.
 * Uses debouncing to avoid excessive API calls while typing.
 *
 * @param debounceMs - Debounce delay in milliseconds (default: 300)
 * @returns Object with password strength data, loading state, and check function
 */
export const useCheckPasswordStrength = (debounceMs = 300) => {
  const [strength, setStrength] = useState<CheckPasswordStrengthResponse | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const checkStrength = useCallback(
    async (password: string, userInputs?: string[]) => {
      // Clear previous timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Abort previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Reset state if password is empty
      if (!password) {
        setStrength(null);
        setIsChecking(false);
        setError(null);
        return;
      }

      setIsChecking(true);

      debounceTimerRef.current = setTimeout(async () => {
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
          const request: CheckPasswordStrengthRequest = {
            password,
            user_inputs: userInputs,
          };
          const response = await authService.checkPasswordStrength(request, {
            signal: controller.signal,
          });

          // Only update state if this request wasn't superseded by a newer one
          if (abortControllerRef.current === controller) {
            setStrength(response);
            setError(null);
          }
        } catch {
          // Ignore if request was aborted (either by signal or superseded by newer request)
          if (controller.signal.aborted) {
            return;
          }
          // Only update state if this request wasn't superseded by a newer one
          if (abortControllerRef.current === controller) {
            setError('Failed to check password strength');
            setStrength(null);
          }
        } finally {
          // Only update loading state if this request wasn't aborted and wasn't superseded
          if (!controller.signal.aborted && abortControllerRef.current === controller) {
            setIsChecking(false);
          }
        }
      }, debounceMs);
    },
    [debounceMs],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const reset = useCallback(() => {
    setStrength(null);
    setIsChecking(false);
    setError(null);
  }, []);

  return {
    strength,
    isChecking,
    error,
    checkStrength,
    reset,
    meetsRequirements: strength?.meets_requirements ?? false,
  };
};

/**
 * Mutation hook for resetting password via a signed link.
 */
export const useResetPasswordWithSignature = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: { sig: string; password: string; confirm_password: string }) =>
      authService.resetPasswordWithSignature(data),
    onSuccess: () => {
      toast.success('Password reset successfully. Please log in with your new password.');
      navigate('/login', { replace: true });
    },
  });
};

/**
 * Mutation hook for verifying email via a signed link.
 */
export const useVerifyEmail = () => {
  return useMutation({
    mutationFn: (data: { sig: string }) => authService.verifyEmail(data),
  });
};
